/**
 * Source: recommendations half of the product-card adapter layer.
 *
 * Same role as webshop_source.js's WebshopSource, for a different backend:
 * WebshopSource's endpoint (webshop.webshop.api.get_product_filter_data) is a
 * generic "list published items matching these filters" query with no concept
 * of "similar to this item" / "based on this cart" / "for this customer" - it
 * cannot serve a recommendation query no matter what filters are passed. This
 * class is the Source for raftor_webhq's purpose-built recommendation
 * endpoints instead, registered on window.rfhqProductCard.adapters.recommendations
 * so "Recommendation" can be a source_type option on Item Grid/Item Carousel
 * blocks exactly like Newest/Featured/Item Group/Brand are.
 *
 * adaptItems() (webshop.js) is pure shape-translation with no coupling to
 * where the rows came from - reused as-is, same as WebshopSource does.
 */

import { adaptItems } from './webshop.js';
import { fetchDecorations, routesAndCodes } from './webshop_source.js';
import { getSwatchColorOverrides } from '../swatch_color_overrides.js';
import { getAttributeIcons } from '../attribute_icons.js';

const M_FOR_ITEM = 'raftor_webhq.catalog.recommendations.get_recommended_items_for';
const M_FOR_CART = 'raftor_webhq.catalog.recommendations.get_recommended_items_for_cart';
const M_FOR_YOU = 'raftor_webhq.catalog.recommendations.get_recommended_items_for_you';
const M_LISTING_SETTINGS = 'webshop.webshop.api.get_product_filter_data';

/**
 * These three endpoints return a plain item list, not WebshopSource's
 * {items, settings} envelope - but adaptItem() needs the real Webshop
 * Settings (settings.enabled, allow_items_not_in_stock, enable_checkout,
 * show_stock_availability, ...) to decide each card's CTA correctly
 * (webshop.js's adaptActionKind: without settings.enabled, EVERY non-variant
 * item is treated as unpurchasable and silently rendered with no button at
 * all, regardless of real stock). Fetched once, from the same lightweight
 * listing endpoint and cached the same way product_variant_swatches.js's own
 * withCartSettings() already does for the identical need, rather than
 * inventing a second settings-fetch convention.
 */
let cachedSettings = null;
let pendingSettings = null;

/**
 * Cache real settings only, never the {} failure fallback.
 *
 * `cachedSettings = ... || {}` used to run unconditionally, and the guard
 * above it was `if (cachedSettings)` - an empty object is truthy in JS, so
 * one transient failure (a slow response, a dropped request, anything
 * call()'s own settle-guarantee turns into `null`) got cached as if it were
 * a real answer and every recommendation card for the rest of the page's
 * life silently rendered against it: enable_checkout undefined → "Add to
 * Quote"/"Go to Quote" instead of "Add to Cart", enabled undefined → any
 * non-variant recommended item falls to ACTION_KIND.SOLDOUT (no button at
 * all) via adaptActionKind's `purchasable = Boolean(settings.enabled) && ...`.
 * No error, no visible failure - the card just quietly showed the wrong CTA,
 * with the real Webshop Settings (enable_checkout: 1 confirmed live) never
 * even re-requested. Only caching a response that actually has fields fixes
 * that: a genuine failure now retries on the next card's fetch instead of
 * poisoning the whole page permanently after the first bad response.
 */
function getSettings() {
	if (cachedSettings) return Promise.resolve(cachedSettings);
	if (pendingSettings) return pendingSettings;

	pendingSettings = call(M_LISTING_SETTINGS, { query_args: { start: 0 } }).then((listing) => {
		const settings = (listing && listing.settings) || {};
		pendingSettings = null;
		if (Object.keys(settings).length) cachedSettings = settings;
		return settings;
	});
	return pendingSettings;
}

/**
 * Promise-wrapped frappe.call guaranteed to settle even if the server throws
 * and frappe.call invokes neither callback nor error - same documented
 * failure mode webshop_source.js's call() guards against, so a broken
 * recommendation source fails to an empty rail rather than a grid that never
 * finishes loading.
 */
function call(method, args) {
	return new Promise((resolve) => {
		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			resolve(value);
		};

		if (typeof frappe === 'undefined' || !frappe.call) {
			done(null);
			return;
		}

		let req;
		try {
			req = frappe.call({ method, args, callback: (r) => done(r && r.message), error: () => done(null) });
		} catch (err) {
			done(null);
			return;
		}

		if (req && typeof req.always === 'function') {
			req.always(() => setTimeout(() => done(null), 0));
		} else {
			setTimeout(() => done(null), 15000);
		}
	});
}

export class RecommendationSource {
	/**
	 * @param {object} [opts]
	 * @param {CartStore} [opts.cartStore] forwarded into fetchDecorations() for
	 *        per-card cart qty, same as WebshopSource.
	 */
	constructor(opts) {
		this.cartStore = (opts || {}).cartStore || null;
	}

	/**
	 * @param {object} query { start, filters }
	 * @param {object} query.filters
	 * @param {'for_item'|'for_cart'|'for_you'} query.filters.mode
	 * @param {string} [query.filters.item_code] required when mode is 'for_item'
	 * @returns {Promise<{models, pagination}>}
	 */
	async fetch(query) {
		const filters = (query && query.filters) || {};
		const mode = filters.mode || 'for_you';

		let method = M_FOR_YOU;
		let args = {};
		let enabledFlag = 'rfhqRecommendationsEnabledHome';
		if (mode === 'for_item' && filters.item_code) {
			method = M_FOR_ITEM;
			args = { item_code: filters.item_code };
			enabledFlag = 'rfhqRecommendationsEnabledPdp';
		} else if (mode === 'for_cart') {
			method = M_FOR_CART;
			enabledFlag = 'rfhqRecommendationsEnabledCart';
		}

		// The one place any surface's on/off state is enforced - PDP/cart's own
		// mount files don't need to know about settings at all, and home's
		// Item Carousel block has no equivalent pre-check to skip (it's part of
		// the server-rendered page). window.rfhq*Enabled is injected by
		// raftor_webhq's storefront_feature_flags.py from Raftor Storefront
		// Feature Settings; a disabled surface fetches nothing and the caller's
		// onEmpty handling (mountProductGrid/mountProductCarousel) removes the
		// block, same as a legitimately-empty result.
		if (typeof window !== 'undefined' && window[enabledFlag] === false) {
			return { models: [], pagination: { hasMore: false } };
		}

		// Not paginated - every one of these three endpoints returns its full,
		// already limit-capped result (server-resolved from Raftor Storefront
		// Feature Settings > Recommendation Fallback Count) in one call.
		const start = (query && query.start) || 0;
		if (start > 0) {
			return { models: [], pagination: { hasMore: false } };
		}

		const [items, fetchedSettings, swatchColorOverrides, attributeIcons] = await Promise.all([
			call(method, args),
			getSettings(),
			getSwatchColorOverrides(),
			getAttributeIcons(),
		]);
		// Shallow copy, not a mutation - getSettings() may return a cached
		// object shared across calls (mirrors withCartSettings-style caching
		// elsewhere in this module), so writing straight onto it would leak
		// this into an unrelated caller's settings.
		const settings = Object.assign({}, fetchedSettings, { swatchColorOverrides, attributeIcons });
		const rows = items || [];
		const { routes, itemCodes } = routesAndCodes(rows);
		const decorations = await fetchDecorations(routes, itemCodes, rows, this.cartStore);
		const models = adaptItems(rows, settings, decorations);

		// WebshopSource.fetch() returns {models, settings, pagination} -
		// settings used to be dropped here, correct only for the initial
		// render (adaptItems() above already baked it into each model's
		// actions.addLabel). grid.js has no idea which Source produced a
		// given grid and treats both identically: `this.settings =
		// result.settings || {}`, so every Recommendation-sourced grid's
		// OWN settings silently stayed {} regardless of whether the fetch
		// above succeeded. That only mattered once a shopper picked a size -
		// _onVariantResolved() rebuilds the actions object from
		// this.settings.enable_checkout, not from the model's already-
		// correct label, and {}.enable_checkout is undefined → falsy → "Add
		// to Quote"/"Go to Quote" on a site where checkout is very much on
		// (confirmed live: enable_checkout: 1). Matching WebshopSource's
		// envelope shape is what actually fixes it, not another cache.
		return { models, settings, pagination: { hasMore: false } };
	}
}
